import { Component, Input, Output, EventEmitter } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ClinicContentBlock } from '../../../data/models/ClinicContentBlock';
import { environment } from '../../../../environments/environment';

interface ImageErrorState {
  url: string
  message: string
}

@Component({
  selector: 'app-block-item',
  imports: [MatButtonModule, MatIconModule, MatSlideToggleModule, MatTooltipModule],
  templateUrl: './block-item.component.html',
  styleUrl: './block-item.component.scss'
})
export class BlockItemComponent {
  @Input() block!: ClinicContentBlock
  @Input() isFirst = false
  @Input() isLast = false
  @Input() blockNumber = 0
  @Input() localLogoUrl: string | null = null

  @Output() moveUp = new EventEmitter<void>()
  @Output() moveDown = new EventEmitter<void>()
  @Output() edit = new EventEmitter<void>()
  @Output() remove = new EventEmitter<void>()
  @Output() togglePublish = new EventEmitter<void>()
  @Output() toggleCollapsible = new EventEmitter<void>()

  get isStandard(): boolean {
    return this.block.block_type.is_standard
  }

  get blockLabel(): string {
    switch (this.block.block_type.name) {
      case 'logo': return 'Logo'
      case 'address': return 'Adresse'
      case 'phone': return 'Zentrale Rufnummer'
      case 'website': return 'Link zur Website'
      case 'text': return 'Textblock ' + this.blockNumber
      case 'video': return 'Videoblock ' + this.blockNumber
      case 'image': return 'Bildblock ' + this.blockNumber
      case 'button': return 'Button ' + this.blockNumber
      case 'contact_person': return 'Ansprechperson ' + this.blockNumber
      default: return this.block.block_type.name
    }
  }

  get toggleTooltip(): string {
    return this.block.is_published
      ? 'Der Beitrag ist veröffentlicht bzw. wird mit Speichern der Seite veröffentlicht.'
      : 'Der Beitrag ist nicht veröffentlicht. Aktiviere den Schalter, um ihn zu veröffentlichen.'
  }

  get collapsibleTooltip(): string {
    return this.block.is_collapsible
      ? 'Der Block wird in der App einklappbar angezeigt. Klicke, um das Einklappen zu deaktivieren.'
      : 'Klicke, um den Block in der App einklappbar anzuzeigen.'
  }

  /** Tracked welche Media-URLs beim Laden gescheitert sind — zur UI-Anzeige und Debugging */
  imageErrors = new Map<string, ImageErrorState>()

  getMediaUrl(path: string | null): string {
    if (!path) return ''
    if (path.startsWith('blob:')) return path
    return `${environment.url}/${environment.backendVersion}/clinic/media/${path}`
  }

  onImageError(path: string | null, event: Event): void {
    if (!path) return
    const url = this.getMediaUrl(path)
    const message = `Bild konnte nicht geladen werden. URL: ${url}`
    this.imageErrors.set(path, { url, message })
    // Hilfreich für DevTools-Debugging — auch in Prod loggen, damit Support es sieht
    console.warn('[block-item] Image load failed', { blockKey: this.block?.key, path, url, event })
  }

  hasImageError(path: string | null): boolean {
    return !!path && this.imageErrors.has(path)
  }

  imageErrorFor(path: string | null): ImageErrorState | undefined {
    return path ? this.imageErrors.get(path) : undefined
  }
}
